import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Truck, Lock, BarChart3, Package, DollarSign, TrendingUp,
  Users, Mail, Eye, Trash2, CheckCircle, CheckCircle2, Clock, XCircle, Zap,
  LogOut, RefreshCw, Car, Wallet, Camera, MapPin, Shield,
  ArrowLeft, Power, CreditCard, Download, Navigation, Loader2,
  ChevronRight, AlertCircle, FileText, Map as MapIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import DispatchTab from "@/components/admin/DispatchTab";
import TripReplayDialog from "@/components/admin/TripReplayDialog";

// Fix default leaflet marker icons (react-leaflet webpack issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Custom driver marker (orange pin)
const driverIcon = new L.DivIcon({
  html: `<div style="background:#f97316;width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;"><span style="transform:rotate(45deg);color:white;font-size:14px;font-weight:bold;">D</span></div>`,
  className: "custom-driver-marker",
  iconSize: [28, 28],
  iconAnchor: [14, 28],
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  pending_payment: "bg-amber-100 text-amber-800",
  confirmed: "bg-green-100 text-green-800",
  assigned: "bg-blue-100 text-blue-800",
  en_route_to_pickup: "bg-indigo-100 text-indigo-800",
  arrived: "bg-purple-100 text-purple-800",
  loaded: "bg-amber-100 text-amber-800",
  in_transit: "bg-orange-100 text-orange-800",
  delivered: "bg-teal-100 text-teal-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-800",
};

const allStatuses = [
  "pending_payment", "confirmed", "assigned", "en_route_to_pickup",
  "arrived", "loaded", "in_transit", "delivered", "completed", "cancelled"
];

// ============ KPI CARD ============
function KPICard({ icon: Icon, label, value, sub, color = "bg-blue-100", iconColor = "text-blue-600" }) {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`h-5 w-5 ${iconColor}`} />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-slate-500 truncate">{label}</p>
            <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-mono)' }}>{value}</p>
            {sub && <p className="text-xs text-slate-400">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============ REVENUE CHART ============
function RevenueChart({ data }) {
  if (!data || data.length === 0) return null;
  const sorted = [...data].sort((a, b) => a._id.localeCompare(b._id));
  const maxRev = Math.max(...sorted.map(d => d.revenue || 0), 1);
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Revenue (Last 7 Days)</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-2 h-32">
          {sorted.map((day) => {
            const height = Math.max(((day.revenue || 0) / maxRev) * 100, 4);
            return (
              <div key={day._id} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[10px] text-slate-500 font-mono">&pound;{(day.revenue || 0).toFixed(0)}</span>
                <div className="w-full bg-orange-500 rounded-t" style={{ height: `${height}%` }} />
                <span className="text-[10px] text-slate-400">{day._id.slice(5)}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ============ POD VIEWER DIALOG ============
function PODViewerDialog({ open, onClose, pod, bookingId }) {
  if (!pod) return null;
  const photoUrl = pod.photo_url ? `${API}/files/${pod.photo_url}` : null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Proof of Delivery</DialogTitle>
          <DialogDescription>Booking: {bookingId?.slice(0, 8).toUpperCase()}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {photoUrl && (
            <img src={photoUrl} alt="POD" className="w-full h-64 object-cover rounded-lg border" />
          )}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-slate-500">Notes:</span> <span className="font-medium">{pod.notes || "None"}</span></div>
            <div><span className="text-slate-500">GPS:</span> <span className="font-medium">{pod.gps_lat ? `${pod.gps_lat.toFixed(4)}, ${pod.gps_lng.toFixed(4)}` : "N/A"}</span></div>
            <div><span className="text-slate-500">Time:</span> <span className="font-medium">{pod.timestamp ? new Date(pod.timestamp).toLocaleString() : "N/A"}</span></div>
            <div><span className="text-slate-500">Confirmed:</span>
              <Badge className={`ml-1 ${pod.confirmed ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
                {pod.confirmed ? "Yes" : "Pending"}
              </Badge>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ MAIN ADMIN PAGE ============
export default function AdminPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("dashboard");
  const [bookings, setBookings] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [emails, setEmails] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDetail, setShowBookingDetail] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [podData, setPodData] = useState(null);
  const [showPOD, setShowPOD] = useState(false);
  const [podBookingId, setPodBookingId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [driverLocations, setDriverLocations] = useState([]);
  const [dispatchData, setDispatchData] = useState(null);
  // Cancel/Reassign dialog state
  const [showCancel, setShowCancel] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelRefund, setCancelRefund] = useState(true);
  const [showReassign, setShowReassign] = useState(false);
  const [bookingToReassign, setBookingToReassign] = useState(null);
  const [reassignDriverId, setReassignDriverId] = useState("");
  // Trip replay dialog
  const [showTripReplay, setShowTripReplay] = useState(false);
  const [tripReplayBookingId, setTripReplayBookingId] = useState(null);

  const auth = useCallback(() => ({ auth: { username: "admin", password } }), [password]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [bookingsRes, analyticsRes, driversRes, payoutsRes, emailsRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`, auth()),
        axios.get(`${API}/admin/analytics`, auth()),
        axios.get(`${API}/admin/drivers`, auth()),
        axios.get(`${API}/admin/payouts`, auth()),
        axios.get(`${API}/admin/emails`, auth()),
      ]);
      setBookings(bookingsRes.data.bookings || []);
      setAnalytics(analyticsRes.data);
      setDrivers(driversRes.data.drivers || []);
      setPayouts(payoutsRes.data.payouts || []);
      setEmails(emailsRes.data.emails || []);
    } catch (error) {
      if (error.response?.status === 401) { setIsAuthenticated(false); return; }
      toast.error("Failed to fetch data");
    } finally { setLoading(false); }
  }, [auth]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsAuthenticated(true);
      toast.success("Login successful");
    } catch { toast.error("Invalid password"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (isAuthenticated) fetchData(); }, [isAuthenticated, fetchData]);

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/status`, { status: newStatus }, auth());
      toast.success(`Status updated`);
      fetchData();
      if (selectedBooking?.booking?.id === bookingId) fetchBookingDetail(bookingId);
    } catch { toast.error("Failed to update"); }
  };

  const fetchBookingDetail = async (bookingId) => {
    try {
      const res = await axios.get(`${API}/admin/bookings/${bookingId}`, auth());
      setSelectedBooking(res.data);
      setShowBookingDetail(true);
    } catch { toast.error("Failed to fetch details"); }
  };

  const deleteBooking = async () => {
    if (!bookingToDelete) return;
    try {
      await axios.delete(`${API}/admin/bookings/${bookingToDelete}`, auth());
      toast.success("Booking deleted");
      setShowDeleteConfirm(false);
      setBookingToDelete(null);
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  const viewPOD = async (bookingId) => {
    try {
      const res = await axios.get(`${API}/admin/pod/${bookingId}`, auth());
      setPodData(res.data);
      setPodBookingId(bookingId);
      setShowPOD(true);
    } catch { toast.error("No POD found"); }
  };

  const confirmPOD = async (bookingId) => {
    try {
      await axios.post(`${API}/admin/pod/${bookingId}/confirm`, {}, auth());
      toast.success("POD confirmed");
    } catch { toast.error("Failed"); }
  };

  const approvePayout = async (payoutId) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/payouts/${payoutId}/approve`, {}, auth());
      toast.success("Payout approved");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setActionLoading(false); }
  };

  const retryPayout = async (payoutId) => {
    try {
      setActionLoading(true);
      const res = await axios.post(`${API}/admin/payouts/${payoutId}/retry`, {}, auth());
      const newStatus = res.data?.payout?.status;
      if (newStatus === "completed") {
        toast.success("Transfer succeeded");
      } else if (newStatus === "awaiting_onboarding") {
        toast.warning("Driver Stripe onboarding still incomplete", {
          description: "Driver must finish Stripe onboarding before payout can complete.",
        });
      } else {
        toast.error("Retry failed", {
          description: res.data?.payout?.last_error_message || "See payouts table for details",
        });
      }
      fetchData();
    } catch (e) {
      toast.error("Retry failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setActionLoading(false);
    }
  };

  const rejectPayout = async (payoutId) => {
    try {
      await axios.post(`${API}/admin/payouts/${payoutId}/reject`, {}, auth());
      toast.success("Payout rejected");
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const approveDriver = async (driverId) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/drivers/${driverId}/approve`, {}, auth());
      toast.success("Driver approved");
      fetchData();
    } catch { toast.error("Failed"); }
    finally { setActionLoading(false); }
  };

  const suspendDriver = async (driverId) => {
    try {
      await axios.post(`${API}/admin/drivers/${driverId}/suspend`, {}, auth());
      toast.success("Driver suspended");
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const unsuspendDriver = async (driverId) => {
    try {
      await axios.post(`${API}/admin/drivers/${driverId}/unsuspend`, {}, auth());
      toast.success("Driver unsuspended");
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const autoDispatch = async (bookingId) => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/admin/bookings/${bookingId}/auto-dispatch`, {}, auth());
      const d = res.data;
      if (d.success) {
        toast.success(`Auto-dispatched to ${d.driver_name}`, {
          description: `Closest driver, ${d.distance_miles} miles away (${d.candidates_considered} considered)`,
        });
        fetchData();
      } else {
        toast.error("Auto-dispatch failed", { description: d.message || d.error_code });
      }
    } catch (e) {
      toast.error("Auto-dispatch failed", { description: e.response?.data?.detail || "Server error" });
    } finally {
      setActionLoading(false);
    }
  };

  const reassignDriver = async (bookingId, driverId) => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/admin/bookings/${bookingId}/assign-driver?driver_id=${driverId}`, {}, auth());
      toast.success(res.data.reassigned ? "Driver reassigned" : "Driver assigned");
      fetchData();
    } catch (e) {
      toast.error("Reassign failed", { description: e.response?.data?.detail || "Server error" });
    } finally {
      setActionLoading(false);
    }
  };

  const cancelBooking = async (bookingId, reason, refund) => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/admin/bookings/${bookingId}/cancel`, { reason, refund }, auth());
      const r = res.data.refund;
      if (r?.refunded) {
        toast.success("Booking cancelled and refunded", { description: `£${r.amount?.toFixed(2)} refunded — ${r.refund_id}` });
      } else if (refund && r?.reason) {
        toast.warning("Booking cancelled — refund failed", { description: r.reason });
      } else {
        toast.success("Booking cancelled");
      }
      fetchData();
    } catch (e) {
      toast.error("Cancel failed", { description: e.response?.data?.detail || "Server error" });
    } finally {
      setActionLoading(false);
    }
  };

  const resendReceipts = async (bookingId) => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/admin/bookings/${bookingId}/resend-receipts`, {}, auth());
      const r = res.data?.results || {};
      const c = r.customer_email;
      const d = r.driver_email;
      const ok = (e) => e && (e.status === "sent" || e.status === "stored");
      if (ok(c) && (!d || ok(d))) {
        toast.success("Receipts resent", {
          description: `Customer: ${c?.to} (${c?.status})${d ? ` | Driver: ${d.to} (${d.status})` : ""}`,
        });
      } else {
        toast.warning("Partial send", {
          description: `customer=${c?.status || "?"} driver=${d?.status || "n/a"}`,
        });
      }
    } catch (e) {
      toast.error("Resend failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setActionLoading(false);
    }
  };

  const forceCompleteBooking = async (bookingId) => {
    if (!window.confirm("Force-complete this booking and release the driver payout? Use only if the driver finished the job but didn't upload POD.")) return;
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/admin/bookings/${bookingId}/force-complete`, {}, auth());
      const p = res.data?.payout;
      if (p?.status === "completed") {
        toast.success("Booking completed — payout released", {
          description: `£${p.driver_amount?.toFixed(2)} to driver (transfer ${p.stripe_transfer_id?.slice(0, 12)}...)`,
        });
      } else if (p?.status === "awaiting_onboarding") {
        toast.warning("Booking completed — payout held", {
          description: "Driver Stripe onboarding incomplete. Payout will release when driver finishes.",
        });
      } else if (p?.status === "failed") {
        toast.error("Booking completed but transfer failed", {
          description: p.last_error_message || "Use Retry button in Payouts tab.",
        });
      } else {
        toast.success("Booking completed");
      }
      fetchData();
    } catch (e) {
      toast.error("Force-complete failed", { description: e.response?.data?.detail || "Server error" });
    } finally {
      setActionLoading(false);
    }
  };

  const fetchDriverLocations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/driver-locations`, auth());
      setDriverLocations(res.data.drivers || []);
    } catch { /* silent */ }
  }, [auth]);

  // Poll driver locations when on Map tab
  useEffect(() => {
    if (!isAuthenticated || tab !== "map") return;
    fetchDriverLocations();
    const interval = setInterval(fetchDriverLocations, 15000);
    return () => clearInterval(interval);
  }, [isAuthenticated, tab, fetchDriverLocations]);

  const downloadPayoutsCSV = async () => {
    try {
      const res = await axios.get(`${API}/admin/payouts/export`, {
        ...auth(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payouts_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const driverStripeBadge = (driver) => {
    if (!driver.stripe_account_id) return { cls: "bg-slate-100 text-slate-500", label: "No Stripe" };
    if (driver.stripe_onboarding_complete) return { cls: "bg-green-100 text-green-700", label: "Stripe ready" };
    if (driver.stripe_details_submitted) return { cls: "bg-amber-100 text-amber-700", label: "Stripe pending" };
    return { cls: "bg-orange-100 text-orange-700", label: "Stripe incomplete" };
  };

  const refreshDriverStripe = async (driverId) => {
    try {
      setActionLoading(true);
      const res = await axios.post(`${API}/admin/drivers/${driverId}/stripe/refresh`, {}, auth());
      const d = res.data;
      if (d.status === "connected") {
        toast.success("Stripe ready", { description: `payouts_enabled=true charges_enabled=true` });
      } else if (d.status === "incomplete") {
        toast.warning("Onboarding incomplete", {
          description: `payouts=${d.payouts_enabled} charges=${d.charges_enabled}${d.disabled_reason ? ` | ${d.disabled_reason}` : ""}`,
        });
      } else if (d.status === "not_connected") {
        toast.info("Driver has not started Stripe onboarding");
      } else {
        toast.error("Refresh failed", { description: d.error || "Stripe API error" });
      }
      fetchData();
    } catch (e) {
      toast.error("Refresh failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setActionLoading(false);
    }
  };

  const refreshAllDriversStripe = async () => {
    try {
      setActionLoading(true);
      const res = await axios.post(`${API}/admin/drivers/stripe/refresh-all`, {}, auth());
      toast.success(`Refreshed ${res.data.refreshed} drivers`, {
        description: res.data.errors > 0 ? `${res.data.errors} errors — see console` : "All up to date",
      });
      if (res.data.errors > 0) console.warn("Refresh-all errors:", res.data.results.filter(r => r.error));
      fetchData();
    } catch (e) {
      toast.error("Refresh-all failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setActionLoading(false);
    }
  };

  const sendStripeOnboardingLink = async (driverId) => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/admin/drivers/${driverId}/stripe-onboarding-link`, {}, auth());
      const d = res.data;
      if (d.success && d.url) {
        // Copy URL to clipboard so admin can share with the driver
        try { await navigator.clipboard.writeText(d.url); } catch {}
        toast.success("Onboarding link copied to clipboard", {
          description: "Send it to the driver — opens Stripe Express onboarding.",
          duration: 7000,
        });
      } else if (d.error_code === "connect_not_enabled") {
        toast.error("Stripe Connect not enabled", { description: d.message });
      } else {
        toast.error("Could not generate link", { description: d.message || d.error_code });
      }
    } catch (e) {
      toast.error("Server error", { description: e.response?.data?.detail || "Try again" });
    } finally {
      setActionLoading(false);
    }
  };

  // ============ LOGIN SCREEN ============
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center">
                <Lock className="h-8 w-8 text-orange-500" />
              </div>
            </div>
            <CardTitle className="text-2xl" style={{ fontFamily: 'var(--font-heading)' }}>Admin Dashboard</CardTitle>
            <CardDescription>Transport Anything 247</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input type="password" data-testid="admin-password-input" placeholder="Enter admin password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" />
              <Button type="submit" data-testid="admin-login-btn" disabled={loading || !password} className="w-full h-12 bg-orange-500 hover:bg-orange-600">
                {loading ? "Logging in..." : "Access Dashboard"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const kpi = analytics?.kpi || {};
  const summary = analytics?.summary || {};

  const tabs = [
    { id: "dashboard", label: "Overview", icon: BarChart3 },
    { id: "jobs", label: "Jobs", icon: Package, badge: kpi.active_jobs },
    { id: "dispatch", label: "Dispatch", icon: Zap },
    { id: "drivers", label: "Drivers", icon: Users, badge: drivers.filter(d => !d.approved && !d.suspended).length },
    { id: "map", label: "Live Map", icon: MapIcon },
    { id: "payouts", label: "Payouts", icon: Wallet, badge: kpi.pending_payout_count },
    { id: "emails", label: "Emails", icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard-v2">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Truck className="h-7 w-7 text-orange-500" />
              <div>
                <span className="text-base font-bold" style={{ fontFamily: 'var(--font-heading)' }}>Admin</span>
                <span className="text-xs text-slate-400 ml-2 hidden sm:inline">Transport Anything 247</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={fetchData} className="text-slate-300 hover:text-white h-8 px-2">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { setIsAuthenticated(false); setPassword(""); }} className="text-slate-300 hover:text-white h-8 px-2">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {/* Tab Navigation */}
          <div className="flex gap-1 -mb-px overflow-x-auto scrollbar-hide">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap relative ${
                  tab === t.id ? "border-orange-500 text-white" : "border-transparent text-slate-400 hover:text-slate-200"
                }`}
                data-testid={`tab-${t.id}`}
              >
                <t.icon className="h-3.5 w-3.5" />
                {t.label}
                {t.badge > 0 && (
                  <span className="ml-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{t.badge}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* ============ OVERVIEW TAB ============ */}
        {tab === "dashboard" && (
          <div className="space-y-6" data-testid="overview-tab">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <KPICard icon={Package} label="Jobs Today" value={kpi.today_jobs || 0} color="bg-blue-100" iconColor="text-blue-600" />
              <KPICard icon={DollarSign} label="Revenue Today" value={`\u00a3${(kpi.today_revenue || 0).toFixed(0)}`} color="bg-green-100" iconColor="text-green-600" />
              <KPICard icon={TrendingUp} label="Platform Fees" value={`\u00a3${(kpi.today_fees || 0).toFixed(0)}`} color="bg-emerald-100" iconColor="text-emerald-600" />
              <KPICard icon={Wallet} label="Pending Payouts" value={`\u00a3${(kpi.pending_payout_amount || 0).toFixed(0)}`} sub={`${kpi.pending_payout_count || 0} pending`} color="bg-orange-100" iconColor="text-orange-600" />
              <KPICard icon={Power} label="Drivers Online" value={`${kpi.online_drivers || 0}/${kpi.approved_drivers || 0}`} color="bg-indigo-100" iconColor="text-indigo-600" />
              <KPICard icon={Navigation} label="Active Jobs" value={kpi.active_jobs || 0} sub={`${kpi.drivers_on_jobs || 0} drivers`} color="bg-purple-100" iconColor="text-purple-600" />
            </div>

            {/* Summary Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <KPICard icon={BarChart3} label="Total Bookings" value={summary.total_bookings || 0} color="bg-slate-100" iconColor="text-slate-600" />
              <KPICard icon={DollarSign} label="Total Revenue" value={`\u00a3${(summary.total_revenue || 0).toLocaleString()}`} color="bg-green-50" iconColor="text-green-600" />
              <KPICard icon={CheckCircle} label="Completed" value={kpi.completed_jobs || 0} color="bg-emerald-50" iconColor="text-emerald-600" />
              <KPICard icon={XCircle} label="Cancelled" value={kpi.cancelled_jobs || 0} color="bg-red-50" iconColor="text-red-600" />
            </div>

            {/* Charts Row */}
            <div className="grid lg:grid-cols-2 gap-6">
              <RevenueChart data={analytics?.daily_stats} />

              {/* Status Distribution */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Bookings by Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {analytics?.bookings_by_status && Object.entries(analytics.bookings_by_status).map(([status, count]) => {
                      const total = summary.total_bookings || 1;
                      return (
                        <div key={status} className="flex items-center gap-2">
                          <Badge className={`text-[10px] ${statusColors[status] || "bg-slate-100"}`}>{status.replace(/_/g, ' ')}</Badge>
                          <div className="flex-1 bg-slate-100 rounded-full h-2">
                            <div className="bg-orange-500 rounded-full h-2" style={{ width: `${(count / total) * 100}%` }} />
                          </div>
                          <span className="text-xs font-mono font-bold text-slate-600 w-6 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ============ JOBS TAB ============ */}
        {tab === "jobs" && (
          <div className="space-y-4" data-testid="jobs-tab">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">All Jobs</h2>
              <span className="text-sm text-slate-500">{bookings.length} total</span>
            </div>
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Ref</TableHead>
                        <TableHead className="text-xs">Customer</TableHead>
                        <TableHead className="text-xs">Route</TableHead>
                        <TableHead className="text-xs">Amount</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                        <TableHead className="text-xs">Driver</TableHead>
                        <TableHead className="text-xs">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.map((b) => (
                        <TableRow key={b.id}>
                          <TableCell className="font-mono text-xs font-medium">{b.id.slice(0, 8).toUpperCase()}</TableCell>
                          <TableCell>
                            <p className="text-sm font-medium">{b.customer_name}</p>
                            <p className="text-xs text-slate-400">{b.customer_email}</p>
                          </TableCell>
                          <TableCell className="text-xs">
                            <p>{b.pickup_address?.slice(0, 25)}...</p>
                            <p className="text-slate-400">→ {b.delivery_address?.slice(0, 25)}...</p>
                          </TableCell>
                          <TableCell className="font-mono text-sm font-bold">&pound;{b.total_cost?.toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge className={`text-[10px] ${statusColors[b.status]}`}>{b.status.replace(/_/g, ' ')}</Badge>
                          </TableCell>
                          <TableCell className="text-xs">
                            {b.assigned_driver_id ? (
                              <span className="text-blue-600">{drivers.find(d => d.id === b.assigned_driver_id)?.name || b.assigned_driver_id.slice(0, 8)}</span>
                            ) : <span className="text-slate-400">Unassigned</span>}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => fetchBookingDetail(b.id)} title="View details" data-testid={`view-booking-${b.id.slice(0, 8)}`}>
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                              {!b.assigned_driver_id && b.status !== "cancelled" && b.status !== "completed" && (
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-indigo-600" onClick={() => autoDispatch(b.id)} disabled={actionLoading} title="Auto-dispatch nearest driver" data-testid={`autodispatch-${b.id.slice(0, 8)}`}>
                                  <Navigation className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {b.status !== "cancelled" && b.status !== "completed" && (
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600" onClick={() => { setBookingToReassign(b); setReassignDriverId(""); setShowReassign(true); }} title={b.assigned_driver_id ? "Reassign driver" : "Assign driver"} data-testid={`reassign-${b.id.slice(0, 8)}`}>
                                  <Users className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {b.status !== "cancelled" && b.status !== "completed" && (
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-amber-600" onClick={() => { setBookingToCancel(b); setCancelReason(""); setCancelRefund(true); setShowCancel(true); }} title="Cancel & refund" data-testid={`cancel-${b.id.slice(0, 8)}`}>
                                  <XCircle className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {(b.status === "delivered" || b.status === "in_transit") && b.assigned_driver_id && (
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600" onClick={() => forceCompleteBooking(b.id)} disabled={actionLoading} title="Force-complete + release driver payout (use if driver didn't upload POD)" data-testid={`force-complete-${b.id.slice(0, 8)}`}>
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {b.status === "completed" && (
                                <>
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-teal-600" onClick={() => viewPOD(b.id)} data-testid={`view-pod-${b.id.slice(0, 8)}`} title="View POD">
                                    <Camera className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600" onClick={() => resendReceipts(b.id)} disabled={actionLoading} title="Resend booking + payout receipts" data-testid={`resend-receipts-${b.id.slice(0, 8)}`}>
                                    <Mail className="h-3.5 w-3.5" />
                                  </Button>
                                </>
                              )}
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => { setBookingToDelete(b.id); setShowDeleteConfirm(true); }} title="Delete">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ============ DRIVERS TAB ============ */}
        {tab === "drivers" && (
          <div className="space-y-4" data-testid="drivers-tab">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">Drivers ({drivers.length})</h2>
              <div className="flex gap-2 text-xs items-center">
                <Badge className="bg-green-100 text-green-800">{drivers.filter(d => d.online).length} online</Badge>
                <Badge className="bg-amber-100 text-amber-800">{drivers.filter(d => !d.approved && !d.suspended).length} pending</Badge>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={refreshAllDriversStripe}
                  disabled={actionLoading}
                  data-testid="refresh-all-stripe-btn"
                  title="Force-refresh every driver's Stripe Connect status from Stripe API"
                >
                  <RefreshCw className="h-3 w-3 mr-1" /> Refresh all Stripe
                </Button>
              </div>
            </div>
            <div className="grid gap-3">
              {drivers.map(driver => (
                <Card key={driver.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${driver.online ? "bg-green-100" : "bg-slate-100"}`}>
                          <Users className={`h-5 w-5 ${driver.online ? "text-green-600" : "text-slate-400"}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-slate-900">{driver.name}</p>
                            {driver.online && <span className="w-2 h-2 rounded-full bg-green-500" />}
                          </div>
                          <p className="text-xs text-slate-500">{driver.email}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                            <span>{driver.phone}</span>
                            <span className="capitalize">{driver.vehicle_type?.replace("_", " ")}</span>
                            <Badge className={`${driverStripeBadge(driver).cls} text-[10px] py-0 h-4`} data-testid={`stripe-badge-${driver.id.slice(0, 6)}`}>
                              <CreditCard className="w-2.5 h-2.5 mr-0.5" />
                              {driverStripeBadge(driver).label}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge className={
                          driver.suspended ? "bg-red-100 text-red-800" :
                          driver.approved ? "bg-green-100 text-green-800" :
                          "bg-amber-100 text-amber-800"
                        }>
                          {driver.suspended ? "Suspended" : driver.approved ? "Active" : "Pending"}
                        </Badge>
                        {driver.last_active && (
                          <span className="text-[10px] text-slate-400">Last: {new Date(driver.last_active).toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                      {!driver.approved && !driver.suspended && (
                        <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700" onClick={() => approveDriver(driver.id)} disabled={actionLoading}>
                          <CheckCircle className="h-3 w-3 mr-1" /> Approve
                        </Button>
                      )}
                      {driver.approved && !driver.suspended && (
                        <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200" onClick={() => suspendDriver(driver.id)}>
                          <XCircle className="h-3 w-3 mr-1" /> Suspend
                        </Button>
                      )}
                      {driver.suspended && (
                        <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700" onClick={() => unsuspendDriver(driver.id)}>
                          <CheckCircle className="h-3 w-3 mr-1" /> Unsuspend
                        </Button>
                      )}
                      {driver.approved && !driver.suspended && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs text-slate-700 border-slate-200"
                          disabled={actionLoading}
                          onClick={() => refreshDriverStripe(driver.id)}
                          data-testid={`stripe-refresh-${driver.id.slice(0, 6)}`}
                          title="Force-refresh this driver's Stripe Connect status from Stripe"
                        >
                          <RefreshCw className="h-3 w-3 mr-1" /> Refresh Stripe
                        </Button>
                      )}
                      {driver.approved && !driver.suspended && !driver.stripe_onboarding_complete && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs text-indigo-700 border-indigo-200"
                          disabled={actionLoading}
                          onClick={() => sendStripeOnboardingLink(driver.id)}
                          data-testid={`stripe-retry-${driver.id.slice(0, 6)}`}
                        >
                          <CreditCard className="h-3 w-3 mr-1" />
                          {driver.stripe_account_id ? "Retry Stripe link" : "Send Stripe link"}
                        </Button>
                      )}
                      <span className="ml-auto text-xs text-slate-500">
                        {driver.total_jobs || 0} jobs | &pound;{(driver.total_earnings || 0).toFixed(2)} earned
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* ============ LIVE MAP TAB ============ */}
        {tab === "map" && (
          <div className="space-y-4" data-testid="map-tab">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">Live Driver Map</h2>
              <div className="flex items-center gap-2">
                <Badge className="bg-green-100 text-green-800">{driverLocations.length} online with GPS</Badge>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={fetchDriverLocations}>
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
                </Button>
              </div>
            </div>
            <Card>
              <CardContent className="p-0 overflow-hidden rounded-lg">
                <div style={{ height: "560px", width: "100%" }} data-testid="driver-map-container">
                  <MapContainer
                    center={[51.5074, -0.1278]}
                    zoom={10}
                    style={{ height: "100%", width: "100%" }}
                    scrollWheelZoom={true}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {driverLocations.map(d => (
                      d.location?.lat && d.location?.lng && (
                        <Marker
                          key={d.id}
                          position={[d.location.lat, d.location.lng]}
                          icon={driverIcon}
                        >
                          <Popup>
                            <div className="text-xs">
                              <p className="font-bold text-slate-900">{d.name}</p>
                              <p className="text-slate-500">{d.phone}</p>
                              <p className="text-slate-500 capitalize">{d.vehicle_type?.replace("_", " ")}</p>
                              {d.active_job ? (
                                <div className="mt-2 pt-2 border-t">
                                  <p className="text-[10px] font-semibold text-orange-600 uppercase">On Job</p>
                                  <p className="text-slate-600">{d.active_job.status?.replace(/_/g, ' ')}</p>
                                </div>
                              ) : (
                                <p className="mt-1 text-green-600 font-medium">Available</p>
                              )}
                              {d.location_updated_at && (
                                <p className="text-[10px] text-slate-400 mt-1">
                                  Updated: {new Date(d.location_updated_at).toLocaleTimeString()}
                                </p>
                              )}
                            </div>
                          </Popup>
                        </Marker>
                      )
                    ))}
                  </MapContainer>
                </div>
              </CardContent>
            </Card>
            {driverLocations.length === 0 && (
              <div className="text-center text-sm text-slate-400 py-4">
                No online drivers are sharing GPS location right now.
              </div>
            )}
          </div>
        )}

        {/* ============ DISPATCH TAB ============ */}
        {tab === "dispatch" && <DispatchTab dispatchData={dispatchData} setDispatchData={setDispatchData} auth={auth} API={API} actionLoading={actionLoading} setActionLoading={setActionLoading} />}

        {/* ============ PAYOUTS TAB ============ */}
        {tab === "payouts" && (
          <div className="space-y-4" data-testid="payouts-tab">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">Payouts</h2>
              <div className="flex items-center gap-2">
                <Badge className="bg-orange-100 text-orange-800">&pound;{(kpi.pending_payout_amount || 0).toFixed(2)} pending</Badge>
                <Button
                  onClick={downloadPayoutsCSV}
                  variant="outline"
                  size="sm"
                  className="text-xs h-8"
                  data-testid="admin-export-payouts-csv-btn"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
                </Button>
              </div>
            </div>
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Driver</TableHead>
                        <TableHead className="text-xs">Booking</TableHead>
                        <TableHead className="text-xs">Gross</TableHead>
                        <TableHead className="text-xs">Fee (25%)</TableHead>
                        <TableHead className="text-xs">Driver Pay</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                        <TableHead className="text-xs">Date</TableHead>
                        <TableHead className="text-xs">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payouts.map(p => (
                        <TableRow key={p.id} data-testid={`payout-row-${p.id.slice(0, 8)}`}>
                          <TableCell className="text-sm font-medium">
                            <div>{p.driver_name || "?"}</div>
                            {p.stripe_status && p.stripe_status !== "connected" && (
                              <div className="text-[10px] text-amber-600 mt-0.5" data-testid={`payout-driver-stripe-${p.id.slice(0, 8)}`}>
                                {p.stripe_status === "not_connected" ? "No Stripe acct" : "Onboarding incomplete"}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{p.booking_id?.slice(0, 8).toUpperCase()}</TableCell>
                          <TableCell className="font-mono text-sm">&pound;{p.gross_amount?.toFixed(2)}</TableCell>
                          <TableCell className="font-mono text-sm text-slate-500">&pound;{p.platform_fee?.toFixed(2)}</TableCell>
                          <TableCell className="font-mono text-sm font-bold text-green-700">&pound;{p.driver_amount?.toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge className={
                              p.status === "completed" ? "bg-green-100 text-green-800" :
                              p.status === "pending" ? "bg-amber-100 text-amber-800" :
                              p.status === "awaiting_onboarding" ? "bg-orange-100 text-orange-800" :
                              p.status === "rejected" ? "bg-slate-100 text-slate-800" :
                              "bg-red-100 text-red-800"
                            } data-testid={`payout-status-${p.id.slice(0, 8)}`}>
                              {p.status === "awaiting_onboarding" ? "Awaiting onboarding" : p.status}
                            </Badge>
                            {p.retry_count > 0 && (
                              <div className="text-[10px] text-slate-500 mt-0.5">Retries: {p.retry_count}</div>
                            )}
                            {p.last_error_message && p.status !== "completed" && (
                              <div className="text-[10px] text-red-600 mt-0.5 max-w-[180px] truncate" title={p.last_error_message}>
                                {p.last_error_message}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            {p.status === "pending" && (
                              <div className="flex gap-1">
                                <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700" onClick={() => approvePayout(p.id)} disabled={actionLoading} data-testid={`approve-payout-${p.id.slice(0, 8)}`}>
                                  Pay
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200" onClick={() => rejectPayout(p.id)}>
                                  Reject
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-teal-600" onClick={() => viewPOD(p.booking_id)}>
                                  <Camera className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            )}
                            {(p.status === "failed" || p.status === "awaiting_onboarding") && (
                              <Button
                                size="sm"
                                className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700"
                                onClick={() => retryPayout(p.id)}
                                disabled={actionLoading}
                                data-testid={`retry-payout-${p.id.slice(0, 8)}`}
                              >
                                Retry
                              </Button>
                            )}
                            {p.stripe_transfer_id && (
                              <span className="text-[10px] text-slate-400 font-mono block mt-1" data-testid={`payout-transfer-id-${p.id.slice(0, 8)}`}>{p.stripe_transfer_id.slice(0, 15)}...</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {payouts.length === 0 && (
                        <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">No payouts yet</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ============ EMAILS TAB ============ */}
        {tab === "emails" && (
          <div className="space-y-4" data-testid="emails-tab">
            <h2 className="text-lg font-bold text-slate-900">Email Log</h2>
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">To</TableHead>
                        <TableHead className="text-xs">Subject</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                        <TableHead className="text-xs">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {emails.map(e => (
                        <TableRow key={e.id}>
                          <TableCell className="text-sm">{e.to}</TableCell>
                          <TableCell className="text-sm max-w-xs truncate">{e.subject}</TableCell>
                          <TableCell>
                            <Badge className={e.status === 'sent' ? 'bg-green-100 text-green-800' : e.status === 'stored' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>{e.status}</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">{new Date(e.created_at).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                      {emails.length === 0 && (
                        <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">No emails</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Booking Detail Dialog */}
      <Dialog open={showBookingDetail} onOpenChange={setShowBookingDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
            <DialogDescription>Ref: {selectedBooking?.booking?.id?.slice(0, 8).toUpperCase()}</DialogDescription>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-slate-500">Customer</p><p className="font-medium text-sm">{selectedBooking.booking.customer_name}</p></div>
                <div><p className="text-xs text-slate-500">Phone</p><p className="font-medium text-sm">{selectedBooking.booking.customer_phone}</p></div>
                <div className="col-span-2"><p className="text-xs text-slate-500">Email</p><p className="font-medium text-sm">{selectedBooking.booking.customer_email}</p></div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs text-slate-500">Pickup</p><p className="text-sm font-medium">{selectedBooking.booking.pickup_address}</p><p className="text-xs text-orange-500">{selectedBooking.quote?.pickup_postcode}</p></div>
                  <div><p className="text-xs text-slate-500">Delivery</p><p className="text-sm font-medium">{selectedBooking.booking.delivery_address}</p><p className="text-xs text-orange-500">{selectedBooking.quote?.delivery_postcode}</p></div>
                </div>
              </div>
              {selectedBooking.quote && (
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div><span className="text-slate-500">Item:</span> <span className="font-medium capitalize">{selectedBooking.quote.item_type}</span></div>
                  <div><span className="text-slate-500">Size:</span> <span className="font-medium capitalize">{selectedBooking.quote.size}</span></div>
                  <div><span className="text-slate-500">Distance:</span> <span className="font-medium">{selectedBooking.quote.distance_miles} mi</span></div>
                </div>
              )}
              <div className="flex items-center justify-between bg-slate-900 text-white rounded-lg p-4">
                <div>
                  <p className="text-xs text-slate-400">Total</p>
                  <p className="text-2xl font-bold font-mono text-orange-500">&pound;{selectedBooking.booking.total_cost?.toFixed(2)}</p>
                </div>
                <Select value={selectedBooking.booking.status} onValueChange={(v) => updateBookingStatus(selectedBooking.booking.id, v)}>
                  <SelectTrigger className="w-44 bg-slate-800 border-slate-700 text-xs h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {allStatuses.map(s => (<SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              {selectedBooking.booking.status === "completed" && (
                <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => viewPOD(selectedBooking.booking.id)}>
                  <Camera className="h-4 w-4 mr-2" /> View POD
                </Button>
              )}
              {/* Trip replay — available whenever a driver has been assigned */}
              {selectedBooking.booking.assigned_driver_id && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-orange-200 text-orange-600 hover:bg-orange-50 ml-2"
                  onClick={() => {
                    setTripReplayBookingId(selectedBooking.booking.id);
                    setShowTripReplay(true);
                  }}
                  data-testid="view-trip-replay-btn"
                >
                  <Navigation className="h-4 w-4 mr-2" /> View Trip Replay
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Booking?</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={deleteBooking}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel & Refund Dialog */}
      <Dialog open={showCancel} onOpenChange={setShowCancel}>
        <DialogContent data-testid="cancel-booking-dialog">
          <DialogHeader>
            <DialogTitle>Cancel Booking</DialogTitle>
            <DialogDescription>
              Booking #{bookingToCancel?.id?.slice(0, 8).toUpperCase()} for &pound;{bookingToCancel?.total_cost?.toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Reason (optional)</label>
              <Input
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g. Customer requested cancellation"
                data-testid="cancel-reason-input"
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={cancelRefund}
                onChange={(e) => setCancelRefund(e.target.checked)}
                className="w-4 h-4 accent-orange-500"
                data-testid="cancel-refund-checkbox"
              />
              <span className="text-slate-700">Refund customer via Stripe</span>
            </label>
            {cancelRefund && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                A full refund of &pound;{bookingToCancel?.total_cost?.toFixed(2)} will be issued to the customer&apos;s original card via Stripe.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancel(false)} disabled={actionLoading}>Keep booking</Button>
            <Button
              variant="destructive"
              disabled={actionLoading}
              onClick={async () => {
                await cancelBooking(bookingToCancel.id, cancelReason || null, cancelRefund);
                setShowCancel(false);
              }}
              data-testid="confirm-cancel-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
              {cancelRefund ? "Cancel & Refund" : "Cancel booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Driver Dialog */}
      <Dialog open={showReassign} onOpenChange={setShowReassign}>
        <DialogContent data-testid="reassign-driver-dialog">
          <DialogHeader>
            <DialogTitle>{bookingToReassign?.assigned_driver_id ? "Reassign Driver" : "Assign Driver"}</DialogTitle>
            <DialogDescription>
              Booking #{bookingToReassign?.id?.slice(0, 8).toUpperCase()}
              {bookingToReassign?.assigned_driver_id && (
                <span className="block mt-1 text-amber-700">
                  Currently assigned to: {drivers.find(d => d.id === bookingToReassign.assigned_driver_id)?.name || "Unknown"}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-xs font-medium text-slate-600">Choose driver</label>
            <Select value={reassignDriverId} onValueChange={setReassignDriverId}>
              <SelectTrigger data-testid="reassign-driver-select"><SelectValue placeholder="Select an approved driver" /></SelectTrigger>
              <SelectContent>
                {drivers.filter(d => d.approved && !d.suspended).map(d => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name} {d.online ? "🟢" : "⚪"} — {d.vehicle_type?.replace("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReassign(false)} disabled={actionLoading}>Cancel</Button>
            <Button
              disabled={actionLoading || !reassignDriverId}
              onClick={async () => {
                await reassignDriver(bookingToReassign.id, reassignDriverId);
                setShowReassign(false);
              }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-reassign-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {bookingToReassign?.assigned_driver_id ? "Reassign" : "Assign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* POD Viewer */}
      <PODViewerDialog open={showPOD} onClose={() => setShowPOD(false)} pod={podData} bookingId={podBookingId} />

      {/* Trip Replay (GPS breadcrumbs) */}
      <TripReplayDialog
        open={showTripReplay}
        onClose={() => setShowTripReplay(false)}
        bookingId={tripReplayBookingId}
        auth={auth}
        API={API}
      />
    </div>
  );
}
